import type { FC } from 'react';

import { cn } from '@/utils/cn.util';
import type { UseMediaGalleryResult } from '@/hooks/useMediaGallery.hook';

interface MediaGalleryProps {
  gallery: UseMediaGalleryResult;
  /** Shown when the item has no media at all. */
  emptyIcon?: string;
  emptyBackground?: string;
  alt: string;
}

const navBtn =
  'absolute top-1/2 -translate-y-1/2 grid h-8 w-8 place-items-center rounded-full bg-black/55 text-[15px] font-bold text-white transition-colors hover:bg-black/75';

/**
 * Every photo plus the video, one at a time.
 *
 * `object-contain` on a neutral backdrop, not `object-cover`: the drawer used to crop a single
 * photo to a 150px band, which for moderation is the opposite of what it needs to do — the part
 * of the image that gets cut is exactly the part someone is hiding something in.
 */
const MediaGallery: FC<MediaGalleryProps> = ({ gallery, emptyIcon, emptyBackground, alt }) => {
  const { slides, index, current, hasMultiple, onPrev, onNext, onSelect } = gallery;

  if (!current) {
    return (
      <div
        className="flex h-[220px] shrink-0 items-center justify-center text-6xl"
        style={{ background: emptyBackground ?? 'var(--color-surface-muted)' }}
      >
        <span>{emptyIcon ?? '🖼'}</span>
      </div>
    );
  }

  return (
    <div className="shrink-0 border-b border-[var(--color-divider)]">
      <div className="relative h-[260px] bg-[var(--color-ink)]">
        {current.kind === 'video' ? (
          <video
            key={current.url}
            src={current.url}
            controls
            playsInline
            className="h-full w-full object-contain"
          />
        ) : (
          <img src={current.url} alt={alt} className="h-full w-full object-contain" />
        )}

        {hasMultiple ? (
          <>
            <button type="button" aria-label="Previous" onClick={onPrev} className={cn(navBtn, 'left-2')}>
              ‹
            </button>
            <button type="button" aria-label="Next" onClick={onNext} className={cn(navBtn, 'right-2')}>
              ›
            </button>
            <span className="absolute bottom-2 right-2 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-semibold text-white">
              {index + 1} / {slides.length}
            </span>
          </>
        ) : null}
      </div>

      {hasMultiple ? (
        <div className="flex gap-1.5 overflow-x-auto bg-[var(--color-surface-muted)] px-3 py-2">
          {slides.map((slide, i) => (
            <button
              key={`${slide.kind}-${slide.url}`}
              type="button"
              onClick={() => onSelect(i)}
              aria-label={`${slide.kind === 'video' ? 'Video' : 'Photo'} ${i + 1}`}
              className={cn(
                'relative h-12 w-12 shrink-0 overflow-hidden rounded-lg border-2 bg-[var(--color-ink)] transition-colors',
                i === index ? 'border-brand-500' : 'border-transparent hover:border-[var(--color-divider)]',
              )}
            >
              {slide.kind === 'video' ? (
                <span className="grid h-full w-full place-items-center bg-[var(--color-surface-muted-2)] text-[15px]">
                  ▶
                </span>
              ) : (
                <img src={slide.url} alt="" className="h-full w-full object-cover" />
              )}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default MediaGallery;
